import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { Profile } from '../../lib/supabase';

interface AdminRouteProps {
  children: React.ReactNode;
}

export default function AdminRoute({ children }: AdminRouteProps) {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const check = async () => {
      // Demo mode bypass — check localStorage first
      const isDemoMode = localStorage.getItem('isDemoMode') === 'true';
      const demoRole = localStorage.getItem('userRole');
      const demoPlan = localStorage.getItem('userPlan');
      if (isDemoMode && (demoRole === 'admin' || demoPlan === 'admin')) {
        setAllowed(true);
        setChecking(false);
        return;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          navigate('/login');
          return;
        }
        const { data } = await supabase
          .from('profiles')
          .select('role, plan')
          .eq('id', session.user.id)
          .maybeSingle();
        const profile = data as Pick<Profile, 'role' | 'plan'> | null;
        if (profile?.role !== 'admin' && profile?.plan !== 'admin') {
          navigate('/dashboard');
          return;
        }
        setAllowed(true);
      } catch {
        navigate('/login');
      } finally {
        setChecking(false);
      }
    };
    check();
  }, [navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <i className="ri-loader-4-line animate-spin text-4xl text-teal-600"></i>
          <p className="text-gray-600 font-medium">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!allowed) return null;

  return <>{children}</>;
}
